import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTerritories } from '../../context/TerritoriesContext';
import { subscribeLeads } from '../../data/listeners/leads';
import { computeScoreboard, scoreboardByTerritory, scoreboardBySource } from '../../data/activity';

const peso = (n) => `₱${Math.round(n).toLocaleString()}`;
const pct = (x) => (x == null ? '—' : `${Math.round(x * 100)}%`);

function Tile({ n, label, color }) {
  return (
    <div className="stat">
      <div className="n" style={color ? { color } : undefined}>{n}</div>
      <div className="l">{label}</div>
    </div>
  );
}

export default function Scoreboard() {
  const { tenantId, role, territoryIds } = useAuth();
  const { territories } = useTerritories();
  const [leads, setLeads] = useState([]);
  const [period, setPeriod] = useState('today'); // Phase 4: Today / Week / Month window
  useEffect(() => subscribeLeads({ tenantId, role, territoryIds }, setLeads), [tenantId, role, territoryIds]);

  const s = useMemo(() => computeScoreboard(leads), [leads]);
  const rows = useMemo(() => scoreboardByTerritory(leads, territories), [leads, territories]);
  // v1.1 source rollup — the card renders only when some lead actually carries a
  // source, so tenants without a chatbot (DoR-33) see no change at all.
  const srcRows = useMemo(() => scoreboardBySource(leads), [leads]);
  const hasSources = srcRows.some((r) => r.key !== 'manual');

  const PERIOD_LABEL = { today: 'today', week: 'this week', month: 'this month' };
  const followupsForPeriod = period === 'week' ? s.followupsThisWeek
    : period === 'month' ? s.followupsThisMonth : s.followupsToday;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
      <div className="toolbar">
        <span style={{ flex: 1 }} />
        <select className="chip-select" aria-label="Scoreboard period" value={period} onChange={(e) => setPeriod(e.target.value)}>
          <option value="today">Today</option>
          <option value="week">This week</option>
          <option value="month">This month</option>
        </select>
      </div>
      <div className="stat-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <Tile n={followupsForPeriod} label={`Follow-ups ${PERIOD_LABEL[period]}`} />
        <Tile n={s.followupsOverdue} label="Overdue" color="var(--presto-error)" />
        <Tile n={s.followupsThisWeek} label="Due this week" />
      </div>
      <div className="stat-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <Tile n={s.activePipeline} label="Active pipeline" />
        <Tile n={s.won} label="Won" color="var(--presto-success)" />
        <Tile n={peso(s.wonValue)} label="Won ₱/mo" color="var(--presto-success)" />
      </div>

      <div>
        <div className="section-title">By territory</div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Territory</th><th>Today</th><th>Overdue</th><th>This week</th><th>Active</th><th>Won</th><th>Won ₱/mo</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontWeight: 'var(--font-semibold)' }}>{r.label}</td>
                  <td>{r.followupsToday}</td>
                  <td style={{ color: r.followupsOverdue ? 'var(--presto-error)' : 'inherit' }}>{r.followupsOverdue}</td>
                  <td>{r.followupsThisWeek}</td>
                  <td>{r.activePipeline}</td>
                  <td>{r.won}</td>
                  <td>{peso(r.wonValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {hasSources && (
        <div>
          <div className="section-title">Lead sources</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Source</th><th>Total</th><th>Won</th><th>Lost</th><th>Close rate</th><th>Conversion</th></tr>
              </thead>
              <tbody>
                {srcRows.map((r) => (
                  <tr key={r.key}>
                    <td style={{ fontWeight: 'var(--font-semibold)' }}>{r.label}</td>
                    <td>{r.total}</td>
                    <td style={{ color: r.won ? 'var(--presto-success)' : 'inherit' }}>{r.won}</td>
                    <td>{r.lost}</td>
                    <td>{pct(r.closeRate)}</td>
                    <td>{pct(r.conversion)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="muted" style={{ fontSize: 'var(--text-caption)', marginTop: 4 }}>
            All time · close rate = won ÷ decided (won + lost) · conversion = won ÷ all leads
          </div>
        </div>
      )}
    </div>
  );
}
